import * as Sentry from "@sentry/react";
import userflow from "userflow.js";
import { getEnv, isProduction, cognitoRegion } from "./env";
import { PostHogService } from "./PostHogService";
import type { ThirdPartyService, UserIdentityParams } from "./types";

// Re-exported for existing consumers (e.g. ThirdPartyProvider.tsx).
export { getEnv, isProduction } from "./env";

declare global {
  interface Window {
    fcSettings: {
      onInit: () => void;
    };
    fcWidget?: {
      setExternalId: (userId: string) => void;
      user?: {
        setEmail: (email: string) => void;
        setProperties: (properties: Record<string, any>) => void;
      };
      open?: () => void;
    };
    fcWidgetMessengerConfig: {
      config: {
        headerProperty: {
          hideChatButton?: boolean;
        };
      };
    };
  }
}

export const openFreshchat = () => {
  window.fcWidget?.open?.();
};

// ============ Third Party Services ============

let sentryInitialized = false;
let sentryIsRealUser = false;

const SentryService: ThirdPartyService = {
  // Initialize Sentry as early as possible (before auth) so the replay
  // integration is recording from page load. Without this, any error thrown
  // during the auth/bootstrap window has no replay buffer to flush, even
  // though `replaysOnErrorSampleRate: 1.0` is set. The `isRealUser` filter
  // is applied at `beforeSend` time, not at init time — non-customer events
  // are still dropped (and their orphan replays are GC'd by Sentry).
  initWhenAuthenticated: false,
  init: (): void => {
    if (sentryInitialized) {
      return;
    }
    sentryInitialized = true;

    Sentry.init({
      dsn: "https://e4a60fdeb737005e9441fe3390c550a1@o4508392148893696.ingest.de.sentry.io/4508392152957008",
      release: getEnv("VITE_APP_VERSION"),
      dist: getEnv("VITE_DOMAIN") || "local",
      integrations: (defaultIntegrations) => {
        const next = [...defaultIntegrations];
        if (isProduction) {
          next.push(Sentry.browserTracingIntegration());
          next.push(
            Sentry.replayIntegration({
              // Flush even very short sessions so an error fired immediately
              // after page load still attaches a replay. Default is 5000ms,
              // which silently drops replays for "error on first paint".
              minReplayDuration: 0,
            })
          );
        }
        return next;
      },
      beforeSend(event) {
        if (!isProduction || !sentryIsRealUser) {
          return null;
        }
        // Drop third-party Userflow script-load failures. The Userflow CDN
        // (js.userflow.com) is regularly blocked by ad-blockers / other
        const exception = event.exception?.values?.[0];
        const frames = exception?.stacktrace?.frames ?? [];
        if (frames.some((f) => f.filename?.includes("userflow"))) {
          return null;
        }
        event.tags = event.tags || {};
        if (event.request?.url) {
          event.tags.url = event.request.url;
        }
        if (event.extra?.account) {
          event.tags.account = event.extra.account as string;
        }
        if (event.extra?.role) {
          event.tags.role = event.extra.role as string;
        }
        if (event.extra?.organization) {
          event.tags.organization = event.extra.organization as string;
        }
        return event;
      },
      tracesSampleRate: 0.05, // 5% is plenty
      replaysSessionSampleRate: 0.01, // 1%
      replaysOnErrorSampleRate: 1.0, // keep all error sessions
      ignoreErrors: [/Could not load Userflow/i, /js\.userflow\.com/i],
    });
  },

  identify: ({
    isRealUser,
    hashedUserId,
    organization,
    account,
    role,
  }: UserIdentityParams): void => {
    // `sentryIsRealUser` gates `beforeSend`. It used to be set during `init`,
    // but now that `init` runs pre-auth we set it here instead. Until this
    // runs, `sentryIsRealUser` is false and `beforeSend` drops events —
    // which is the desired behaviour for pre-auth errors *from non-real
    // users* (and a known tradeoff for pre-auth errors from real users,
    // who will only start sending once they've fully authenticated).
    sentryIsRealUser = isRealUser;
    Sentry.setUser({ user: hashedUserId });
    Sentry.setExtra("organization", organization);
    Sentry.setExtra("account", account);
    Sentry.setExtra("role", role);
  },
  teardown: (): void => {
    sentryIsRealUser = false;
  },
};

const UserflowService: ThirdPartyService = {
  initWhenAuthenticated: false,
  init: (): void => {
    if (isProduction) {
      userflow.init(getEnv("VITE_USERFLOW_TOKEN"));
    }
  },

  identify: ({
    hashedUserId,
    organization,
    account,
    role,
    theme,
  }: UserIdentityParams): void => {
    if (isProduction) {
      userflow.identify(hashedUserId, {
        name: hashedUserId,
        customer_organization: organization,
        customer_account: account,
        region: cognitoRegion,
        role,
        theme,
      });
    }
  },
  teardown: (): void => {
    userflow.endAll();
  },
};

const FreshchatService: ThirdPartyService = {
  initWhenAuthenticated: false,
  init: (): void => {
    //Freshchat - hide chat button by default, regardless of the user's authentication state
    window.fcWidgetMessengerConfig = {
      config: {
        headerProperty: {
          hideChatButton: true,
        },
      },
    };
  },

  identify: ({ email, userId, role }: UserIdentityParams): void => {
    window.fcSettings = {
      onInit: () => {
        window.fcWidget?.setExternalId(userId);
        window.fcWidget?.user?.setEmail(email);
        window.fcWidget?.user?.setProperties({
          role,
        });
      },
    };
  },
  teardown: (): void => {},
};

// ============ Seem Third Party Services ============
// Notice: Document360 is not included in this list as it is initialized by
// document360Widget.ts, loaded as its own entry module from index.html
const SeemThirdPartyServices: ThirdPartyService[] = [
  SentryService,
  UserflowService,
  FreshchatService,
  PostHogService,
];

// ============ Services Functions ============

export const initAllNonAuthenticatedServices = (): void => {
  SeemThirdPartyServices.forEach((service) => {
    if (!service.initWhenAuthenticated) {
      service.init();
    }
  });
};

export const initAndIdentifyAllServices = (
  params: UserIdentityParams
): void => {
  SeemThirdPartyServices.forEach((service) => {
    if (service.initWhenAuthenticated) {
      service.init(params);
    }
    service.identify(params);
  });
};

export const teardownAllServices = (): void => {
  SeemThirdPartyServices.forEach((service) => {
    service.teardown();
  });
};
